/**
 * PLI (Public Liability Insurance) renewal — frontend API calls.
 *
 * Covers the contractor-scoped endpoints (/api/contractors/:id/pli…), the admin
 * endpoints (/api/admin/pli…), and the public magic-link endpoints (no auth).
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const getAuthToken = () => localStorage.getItem("token");

async function request(path, { method = "GET", body, auth = true } = {}) {
  const isFormData = body instanceof FormData;
  const token = getAuthToken();
  const headers = {
    ...(auth && token ? { Authorization: `Bearer ${token}` } : {}),
    ...(isFormData || body == null ? {} : { "Content-Type": "application/json" }),
  };
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    ...(body != null ? { body: isFormData ? body : JSON.stringify(body) } : {}),
  });
  let data;
  const ct = res.headers.get("content-type");
  if (ct && ct.includes("application/json")) data = await res.json();
  else data = { success: res.ok, message: await res.text() };
  if (!res.ok) return { success: false, status: res.status, ...data };
  return data;
}

// ── contractor-scoped ────────────────────────────────────────────────────────
export const getContractorPli = (id) => request(`/api/contractors/${id}/pli`);

export const uploadContractorPli = (id, formData) =>
  request(`/api/contractors/${id}/pli`, { method: "POST", body: formData });

export const setPliExpiry = (id, expiry_date) =>
  request(`/api/contractors/${id}/pli/expiry`, { method: "POST", body: { expiry_date } });

export const promotePli = (id, { source_document_id, expiry_date, issue_date }) =>
  request(`/api/contractors/${id}/pli/promote`, {
    method: "POST",
    body: { source_document_id, expiry_date, issue_date },
  });

export const approvePli = (id, docId) =>
  request(`/api/contractors/${id}/pli/${docId}/approve`, { method: "POST" });

export const rejectPli = (id, docId, reason) =>
  request(`/api/contractors/${id}/pli/${docId}/reject`, { method: "POST", body: { reason } });

// ── admin ────────────────────────────────────────────────────────────────────
export const getPliReviewQueue = () => request(`/api/admin/pli/review-queue`);

export const getPliDashboard = (params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== "")
  ).toString();
  return request(`/api/admin/pli/dashboard${qs ? `?${qs}` : ""}`);
};

export const sendPliReminder = (contractorId) =>
  request(`/api/admin/pli/${contractorId}/send-reminder`, { method: "POST" });

export const requestPli = (contractorId) =>
  request(`/api/admin/pli/${contractorId}/request-pli`, { method: "POST" });

// ── public magic-link (no auth) ──────────────────────────────────────────────
export const validatePliToken = (token) =>
  request(`/api/contractors/pli/${token}`, { auth: false });

export const publicUploadPli = (token, formData) =>
  request(`/api/contractors/pli/${token}`, { method: "POST", body: formData, auth: false });

// ── shared status-badge helper ───────────────────────────────────────────────
export const PLI_STATUS_META = {
  active: { text: "Valid", color: "#1a9c4c" },
  due_soon: { text: "Due soon", color: "#e0a800" },
  lapsed: { text: "Lapsed", color: "#d33" },
  missing: { text: "Missing", color: "#888" },
};

export const pliStatusBadge = (status) => {
  const m = PLI_STATUS_META[status] || { text: status || "—", color: "#888" };
  return m;
};
